export type PageItem = number | 'ellipsis-left' | 'ellipsis-right'

const range = (start: number, end: number): number[] => Array.from({ length: end - start + 1 }, (_, i) => start + i)

// Build the page-number cells for the pagination control. Emits a CONSTANT number of cells (capped
// at `maxItems`) so the centered row keeps a stable width within a breakpoint and doesn't shift as
// you navigate. `maxItems` shrinks on smaller screens (see usePaginationSlotCount) so the control
// stays compact and never pushes the action-bar buttons off screen.
export const buildPageItems = (current: number, total: number, maxItems = 7): PageItem[] => {
    const cap = Math.max(1, maxItems)
    if (total <= cap) return range(1, total)

    // Compact screens: a simple consecutive window (no first/last + ellipsis) — the First/Last arrow
    // buttons still allow jumping. Keeps exactly `cap` cells.
    if (cap < 5) {
        const start = Math.min(Math.max(current - Math.floor((cap - 1) / 2), 1), total - cap + 1)
        return range(start, start + cap - 1)
    }

    // Roomy screens: first + last always shown, a window in between, ellipses bridging the gaps.
    const lead = cap - 2 // count of leading / trailing page numbers shown next to first / last

    // Near the start: [1 … lead, …, total]
    if (current <= lead - 1) {
        return [...range(1, lead), 'ellipsis-right', total]
    }

    // Near the end: [1, …, total-lead+1 … total]
    if (current >= total - (lead - 2)) {
        return [1, 'ellipsis-left', ...range(total - lead + 1, total)]
    }

    // Middle: [1, …, window around current, …, total]. The window holds exactly cap-4 numbers
    // (so the total stays at `cap` for both odd and even caps), centred on the current page.
    const windowSize = cap - 4
    const start = current - Math.floor((windowSize - 1) / 2)
    return [1, 'ellipsis-left', ...range(start, start + windowSize - 1), 'ellipsis-right', total]
}
